<template>
  <div class="fund-nav-chart">
    <div class="fund-nav-chart__main">
      <div
        v-if="yAxisLabels.length"
        class="fund-nav-chart__y-axis"
      >
        <span
          class="fund-nav-chart__y-axis-size"
          aria-hidden="true"
        >{{ yAxisLabelSizer }}</span>
        <span
          v-for="(item, index) in yAxisLabels"
          :key="`y-${index}`"
          class="fund-nav-chart__y-axis-label"
          :style="{ top: `${(item.y / navChart.chartHeight) * 100}%` }"
        >{{ item.label }}</span>
      </div>
      <div class="fund-nav-chart__plot">
        <p
          v-if="!hasData"
          class="fund-nav-chart__empty"
        >{{ $t("privateFund.performanceChartNoData") }}</p>
        <div class="fund-nav-chart__plot-canvas">
          <div
            v-if="activeChartPoint"
            class="fund-nav-chart__tooltip"
            :style="chartTooltipStyle"
          >
            <p class="fund-nav-chart__tooltip-date">{{ activeChartPoint.dateLabel }}</p>
            <p class="fund-nav-chart__tooltip-value">
              {{ $t("privateFund.navMiniChartTooltipValue", { value: activeChartPoint.navPerShareDisplay }) }}
            </p>
          </div>
          <svg
            ref="chartSvg"
            class="fund-nav-chart__svg"
            :class="{ 'is-empty': !hasData }"
            viewBox="0 0 300 160"
            preserveAspectRatio="none"
            aria-hidden="true"
            @touchstart.prevent="handleChartPointer"
            @touchmove.prevent="handleChartPointer"
            @touchend="handleChartPointerLeave"
            @mousedown="handleChartPointer"
            @mousemove="handleChartPointerMove"
            @mouseup="handleChartPointerLeave"
            @mouseleave="handleChartPointerLeave"
          >
            <defs>
              <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#1353c8" stop-opacity="0.18" />
                <stop offset="100%" stop-color="#1353c8" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect
              class="fund-nav-chart__hit"
              x="0"
              y="0"
              width="300"
              height="160"
            />
            <line
              v-for="y in gridLines"
              :key="y"
              class="fund-nav-chart__grid"
              x1="0"
              :y1="y"
              x2="300"
              :y2="y"
            />
            <path
              v-if="chartPoints.length"
              class="fund-nav-chart__area"
              :d="areaPath"
              :fill="`url(#${gradientId})`"
            />
            <path
              v-if="chartPoints.length"
              class="fund-nav-chart__line"
              :d="linePath"
            />
            <line
              v-if="activeChartPoint"
              class="fund-nav-chart__guide"
              :x1="activeChartPoint.x"
              :y1="navChart.plotTop"
              :x2="activeChartPoint.x"
              :y2="navChart.plotBottom"
            />
          </svg>
          <span
            v-if="displayChartPoint"
            class="fund-nav-chart__dot"
            :class="{ 'is-active': !!activeChartPoint }"
            :style="chartDotStyle"
          />
        </div>
        <div
          v-if="axisLabels.length"
          class="fund-nav-chart__axis"
        >
          <span
            v-for="(item, index) in axisLabels"
            :key="`x-${index}`"
            class="fund-nav-chart__axis-label"
            :class="`is-align-${item.align}`"
            :style="{ left: `${item.xPercent}%` }"
          >{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buildNavChartFromList,
  findNearestNavChartPointIndex,
  resolveNavChartTooltipStyle
} from "../utils";

const NAV_CHART_HEIGHT = 160;
const CHART_TOOLTIP_HIDE_DELAY = 1000;

function buildNavChartLinePath(points) {
  if (!Array.isArray(points) || !points.length) {
    return "";
  }
  return points
    .reduce((path, point, index) => {
      const segment = `${index === 0 ? "M" : "L"}${point.x},${point.y}`;
      return `${path} ${segment}`;
    }, "")
    .trim();
}

function buildNavChartAreaPath(points, chartHeight = NAV_CHART_HEIGHT) {
  if (!Array.isArray(points) || !points.length) {
    return "";
  }
  const linePath = buildNavChartLinePath(points);
  const lastPoint = points[points.length - 1];
  return `${linePath} L${lastPoint.x},${chartHeight} L0,${chartHeight} Z`;
}

export default {
  name: "FundNavChart",
  props: {
    navList: {
      type: Array,
      default: () => []
    },
    chartKey: {
      type: [String, Number],
      default: "default"
    }
  },
  data() {
    return {
      selectedChartIndex: -1,
      isChartPointerActive: false,
      chartTooltipHideTimer: null
    };
  },
  computed: {
    navChart() {
      return buildNavChartFromList(this.navList);
    },
    hasData() {
      return this.chartPoints.length > 0;
    },
    chartPoints() {
      return this.navChart.points;
    },
    gridLines() {
      return this.navChart.gridLines;
    },
    axisLabels() {
      return this.navChart.axisLabels;
    },
    yAxisLabels() {
      return this.navChart.yAxisLabels;
    },
    yAxisLabelSizer() {
      return this.yAxisLabels.reduce(
        (max, item) => (item.label.length > max.length ? item.label : max),
        ""
      );
    },
    linePath() {
      return buildNavChartLinePath(this.chartPoints);
    },
    areaPath() {
      return buildNavChartAreaPath(
        this.chartPoints,
        this.navChart.chartHeight
      );
    },
    activeChartPoint() {
      const { points } = this.navChart;
      if (!points.length || this.selectedChartIndex < 0) {
        return null;
      }
      return points[this.selectedChartIndex] || null;
    },
    displayChartPoint() {
      if (this.activeChartPoint) {
        return this.activeChartPoint;
      }
      const { points } = this.navChart;
      return points.length ? points[points.length - 1] : null;
    },
    chartDotStyle() {
      const point = this.displayChartPoint;
      if (!point || !this.navChart.chartWidth || !this.navChart.chartHeight) {
        return {};
      }
      const leftPercent = (point.x / this.navChart.chartWidth) * 100;
      const topPercent = (point.y / this.navChart.chartHeight) * 100;
      return {
        left: `${leftPercent}%`,
        top: `${topPercent}%`
      };
    },
    chartTooltipStyle() {
      return resolveNavChartTooltipStyle(
        this.activeChartPoint,
        this.navChart.chartWidth,
        this.navChart.chartHeight
      );
    },
    gradientId() {
      return `private-fund-nav-chart-${this.chartKey}`;
    }
  },
  watch: {
    navList() {
      this.resetChartSelection();
    }
  },
  beforeDestroy() {
    this.clearChartTooltipHideTimer();
  },
  methods: {
    clearChartTooltipHideTimer() {
      if (this.chartTooltipHideTimer) {
        clearTimeout(this.chartTooltipHideTimer);
        this.chartTooltipHideTimer = null;
      }
    },
    scheduleChartTooltipHide() {
      this.clearChartTooltipHideTimer();
      this.chartTooltipHideTimer = setTimeout(() => {
        this.chartTooltipHideTimer = null;
        this.selectedChartIndex = -1;
      }, CHART_TOOLTIP_HIDE_DELAY);
    },
    resetChartSelection() {
      this.clearChartTooltipHideTimer();
      this.selectedChartIndex = -1;
      this.isChartPointerActive = false;
    },
    resolveChartClientX(event) {
      const svg = this.$refs.chartSvg;
      if (!svg) {
        return null;
      }
      const rect = svg.getBoundingClientRect();
      if (!rect.width) {
        return null;
      }
      let clientX = event.clientX;
      if (event.touches && event.touches.length) {
        clientX = event.touches[0].clientX;
      } else if (event.changedTouches && event.changedTouches.length) {
        clientX = event.changedTouches[0].clientX;
      }
      const ratio = (clientX - rect.left) / rect.width;
      const clampedRatio = Math.min(Math.max(ratio, 0), 1);
      return clampedRatio * (this.navChart.chartWidth || 300);
    },
    updateChartSelectionByEvent(event) {
      const points = this.navChart.points;
      if (!points.length) {
        return;
      }
      const chartX = this.resolveChartClientX(event);
      if (chartX === null) {
        return;
      }
      const index = findNearestNavChartPointIndex(points, chartX);
      if (index >= 0) {
        this.selectedChartIndex = index;
      }
    },
    handleChartPointer(event) {
      if (!this.hasData) {
        return;
      }
      this.clearChartTooltipHideTimer();
      this.isChartPointerActive = true;
      this.updateChartSelectionByEvent(event);
    },
    handleChartPointerMove(event) {
      if (!this.isChartPointerActive || !this.hasData) {
        return;
      }
      this.updateChartSelectionByEvent(event);
    },
    handleChartPointerLeave() {
      this.isChartPointerActive = false;
      if (this.selectedChartIndex >= 0) {
        this.scheduleChartTooltipHide();
      }
    }
  }
};
</script>

<style scoped lang="less">
.fund-nav-chart {
  width: 100%;
  overflow: visible;

  &__main {
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }

  &__y-axis {
    position: relative;
    flex: 0 0 auto;
    height: 280px;
  }

  &__y-axis-size {
    display: block;
    visibility: hidden;
    height: 0;
    overflow: hidden;
    font-size: 20px;
    line-height: 28px;
    white-space: nowrap;
  }

  &__y-axis-label {
    position: absolute;
    right: 0;
    color: #9ca3af;
    font-size: 20px;
    line-height: 28px;
    text-align: right;
    white-space: nowrap;
    transform: translateY(-50%);
  }

  &__plot {
    position: relative;
    flex: 1;
    min-width: 0;
    overflow: visible;
  }

  &__plot-canvas {
    position: relative;
    overflow: visible;
  }

  &__empty {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    color: #9ca3af;
    font-size: 24px;
    line-height: 32px;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &__tooltip {
    position: absolute;
    z-index: 2;
    min-width: 140px;
    padding: 12px 24px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
    pointer-events: none;
  }

  &__tooltip-date {
    margin-bottom: 4px;
    color: #6b7280;
    font-size: 24px;
    line-height: 32px;
    white-space: nowrap;
  }

  &__tooltip-value {
    color: #1353c8;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    white-space: nowrap;
  }

  &__svg {
    display: block;
    width: 100%;
    height: 280px;
    overflow: visible;
    touch-action: none;

    &.is-empty {
      pointer-events: none;
    }
  }

  &__hit {
    fill: transparent;
    cursor: crosshair;
  }

  &__grid {
    stroke: #f0f0f0;
    stroke-width: 1;
    stroke-dasharray: 6 6;
  }

  &__area {
    fill-opacity: 1;
  }

  &__line {
    fill: none;
    stroke: #1353c8;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &__dot {
    position: absolute;
    z-index: 1;
    width: 8px;
    height: 8px;
    margin-left: -4px;
    margin-top: -4px;
    border-radius: 50%;
    background: #1353c8;
    pointer-events: none;
  }

  &__guide {
    stroke: #1353c8;
    stroke-width: 1;
    stroke-dasharray: 6 6;
    opacity: 0.35;
  }

  &__axis {
    position: relative;
    height: 28px;
    margin-top: 12px;
    width: 100%;
  }

  &__axis-label {
    position: absolute;
    top: 0;
    color: #9ca3af;
    font-size: 20px;
    line-height: 28px;
    white-space: nowrap;

    &.is-align-start {
      text-align: left;
      transform: translateX(-8px);
    }

    &.is-align-center {
      text-align: center;
      transform: translateX(-50%);
    }

    &.is-align-end {
      text-align: right;
      transform: translateX(calc(-100% + 8px));
    }
  }
}

.black-theme {
  .fund-nav-chart__y-axis-label,
  .fund-nav-chart__axis-label,
  .fund-nav-chart__empty {
    color: #9d9d9f;
  }

  .fund-nav-chart__tooltip {
    background: #323442;
    border-color: #4b4d5c;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
  }

  .fund-nav-chart__tooltip-date {
    color: #9d9d9f;
  }

  .fund-nav-chart__tooltip-value {
    color: #377cfb;
  }

  .fund-nav-chart__grid {
    stroke: #323442;
  }

  .fund-nav-chart__line {
    stroke: #377cfb;
  }

  .fund-nav-chart__guide {
    stroke: #377cfb;
  }

  .fund-nav-chart__dot {
    background: #377cfb;
  }
}
</style>
